// Scene-aware canvas renderer: traverses Scene + LayoutMap and paints each
// layer through the engine's ScenePainter (one wasm call per layer; the
// per-pixel loop lives in crates/graph/src/scene_painter.rs).

import { planLayerPaint, ScenePainter } from 'fluveria-engine-wasm';
import type { Scene, SceneNodeId, LayerNode } from '../scene/sceneState.js';
import type { LayoutResult, NodeLayout, SceneLayoutContext } from '../scene/layoutEngine.js';
import type { GraphRuntimeResolver, GraphRuntime, LayerPaintArgs, Size, Space } from '../graph/graphRuntime.js';
import type { ColormapName } from '../math/colormaps.js';
import {
  type SdfTheme,
  ISO_FREQUENCY, ISO_HALF_WIDTH, ISO_INTENSITY, STROKE_HALF_WIDTH,
} from './sdfTheme.js';
import { computeLayout, nodePath, placementPrefix, shownInSession } from '../scene/layoutEngine.js';
import type { ArcCoverageMode } from '../sdf/arcCoverageMode.js';
import { isPerfLogEnabled } from '../debug/perfLog.js';
import { mergeRuntimeParamOverrides } from './runtimeParamPrecedence.js';
import { worldScaleOf } from '../scene/layoutMath.js';
import { NO_CLIP, clipInLayer, clipWithin, type ClipBox, type ClipRect, type SceneClip } from '../scene/clip.js';

interface DrawItem {
  /** Stable identity across frames: scene-node id path (instance ids prefixed). */
  itemKey: string;
  layerNode: LayerNode;
  layout: NodeLayout;
  runtime: GraphRuntime;
  /** What the clipping frames above clip the layer to: a scissor, and its box where a frame is turned. */
  clip: SceneClip;
  paramOverrides: Record<string, unknown>;
}

type DrawBounds = [number, number, number, number];

export interface PaintedRect { x0: number; y0: number; x1: number; y1: number; }

/**
 * Everything that determines a draw item's painted pixels. Object fields are
 * compared by reference — the editor updates params/graph arrays immutably
 * (the same contract the runtime's compile caches rely on); paramOverrides
 * additionally falls back to shallow value equality because the standalone
 * viewport rebuilds its scene nodes every frame.
 */
export interface ItemSnapshot {
  /** Exact rect passed to paintTile (includes draw-bounds dilation + clips). */
  paintedRect: PaintedRect;
  /**
   * Merged param values for the keys this layer's graphs actually read
   * (runtime.paramKeys) — a global params replacement only dirties layers
   * whose own values changed. Null when the runtime can't report its keys;
   * then the paramsRef/overridesRef identity fallback applies.
   */
  paramValues: Record<string, unknown> | null;
  paramsRef: Record<string, unknown>;
  overridesRef: Record<string, unknown> | undefined;
  contentKey: GraphRuntime['contentKey'];
  lfw: [number, number, number, number, number, number];
  allocatedW: number;
  allocatedH: number;
  offsetX: number;
  offsetY: number;
  wsX: number;
  wsY: number;
  density: number;
  layerFw: number;
  fit: LayerNode['fit'];
  clipRect: ClipRect | null;
  /** The clip in the layer's own units a turned clipping frame makes (`NO_LOCAL_CLIP`: none). */
  clipLocal: ClipBox;
}

interface PainterCache {
  painter: ScenePainter;
  settingsKey: string;
  snapshots: Map<string, ItemSnapshot>;
  order: string[];
}

const painterCaches = new WeakMap<HTMLCanvasElement, PainterCache>();

/**
 * Backend switches replace the canvas element, so a cache can become
 * unreachable without a final render — free the wasm framebuffer when its
 * cache is collected (same pattern as graphRuntime's handle finalizer).
 */
const painterFinalizer = typeof FinalizationRegistry === 'undefined'
  ? null
  : new FinalizationRegistry<ScenePainter>(painter => {
      painter.free();
    });

/** Drop all persistent painters/snapshots (next render repaints fully). */
export function resetCanvasSceneRendererCache(): void {
  // WeakMap cannot be iterated; bump a generation so settingsKey mismatches.
  cacheGeneration += 1;
}
let cacheGeneration = 0;

function pickParamValues(
  paramKeys: ReadonlySet<string> | undefined,
  mergedParams: Record<string, unknown>,
): Record<string, unknown> | null {
  if (!paramKeys) return null;
  const values: Record<string, unknown> = {};
  for (const key of paramKeys) {
    values[key] = mergedParams[key];
  }
  return values;
}

function shallowEqualRecords(
  a: Record<string, unknown> | undefined,
  b: Record<string, unknown> | undefined,
): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  const aKeys = Object.keys(a);
  if (aKeys.length !== Object.keys(b).length) return false;
  return aKeys.every(key => Object.is(a[key], b[key]));
}

function clipRectsEqual(a: ClipRect | null, b: ClipRect | null): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  return a.x0 === b.x0 && a.y0 === b.y0 && a.x1 === b.x1 && a.y1 === b.y1;
}

function rectsEqual(a: PaintedRect, b: PaintedRect): boolean {
  return a.x0 === b.x0 && a.y0 === b.y0 && a.x1 === b.x1 && a.y1 === b.y1;
}

function contentKeysEqual(a: GraphRuntime['contentKey'], b: GraphRuntime['contentKey']): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  return a.geometryNodes === b.geometryNodes && a.geometryEdges === b.geometryEdges && a.material === b.material;
}

function snapshotChanged(prev: ItemSnapshot, next: ItemSnapshot): boolean {
  if (!rectsEqual(prev.paintedRect, next.paintedRect)) return true;
  if (prev.paramValues && next.paramValues) {
    if (!shallowEqualRecords(prev.paramValues, next.paramValues)) return true;
  } else {
    if (prev.paramsRef !== next.paramsRef) return true;
    if (prev.overridesRef !== next.overridesRef && !shallowEqualRecords(prev.overridesRef, next.overridesRef)) return true;
  }
  if (!contentKeysEqual(prev.contentKey, next.contentKey)) return true;
  for (let i = 0; i < 6; i += 1) {
    if (prev.lfw[i] !== next.lfw[i]) return true;
  }
  return prev.allocatedW !== next.allocatedW
    || prev.allocatedH !== next.allocatedH
    || prev.offsetX !== next.offsetX
    || prev.offsetY !== next.offsetY
    || prev.wsX !== next.wsX
    || prev.wsY !== next.wsY
    || prev.density !== next.density
    || prev.layerFw !== next.layerFw
    || prev.fit !== next.fit
    || !clipRectsEqual(prev.clipRect, next.clipRect)
    || prev.clipLocal.some((value, index) => value !== next.clipLocal[index]);
}

function unionRect(target: PaintedRect | null, rect: PaintedRect): PaintedRect {
  if (rect.x1 <= rect.x0 || rect.y1 <= rect.y0) return target ?? rect;
  if (!target || target.x1 <= target.x0 || target.y1 <= target.y0) return { ...rect };
  return {
    x0: Math.min(target.x0, rect.x0),
    y0: Math.min(target.y0, rect.y0),
    x1: Math.max(target.x1, rect.x1),
    y1: Math.max(target.y1, rect.y1),
  };
}

/**
 * Diff this frame's item snapshots against the previous frame.
 * Returns 'full' (repaint everything), null (nothing changed), or the
 * dirty union rect. Reorders of items present in both frames force a full
 * repaint (compositing order changed); plain adds/removes contribute rects.
 */
export function computeDirtyRegion(
  prevSnapshots: Map<string, ItemSnapshot>,
  prevOrder: string[],
  nextEntries: Array<{ itemKey: string; snapshot: ItemSnapshot }>,
): PaintedRect | 'full' | null {
  const nextKeys = new Set(nextEntries.map(entry => entry.itemKey));
  const commonPrev = prevOrder.filter(key => nextKeys.has(key));
  const commonNext = nextEntries.map(entry => entry.itemKey).filter(key => prevSnapshots.has(key));
  if (commonPrev.length === commonNext.length) {
    for (let i = 0; i < commonPrev.length; i += 1) {
      if (commonPrev[i] !== commonNext[i]) return 'full';
    }
  } else {
    // Duplicate keys collapsed differently — be safe.
    return 'full';
  }

  let dirty: PaintedRect | null = null;
  for (const { itemKey, snapshot } of nextEntries) {
    const prev = prevSnapshots.get(itemKey);
    if (!prev) {
      dirty = unionRect(dirty, snapshot.paintedRect);
    } else if (snapshotChanged(prev, snapshot)) {
      dirty = unionRect(dirty, prev.paintedRect);
      dirty = unionRect(dirty, snapshot.paintedRect);
    }
  }
  for (const key of prevOrder) {
    if (!nextKeys.has(key)) {
      const prev = prevSnapshots.get(key);
      if (prev) dirty = unionRect(dirty, prev.paintedRect);
    }
  }
  return dirty;
}

function dilateDrawBounds(bounds: DrawBounds, dilationPx: number): DrawBounds {
  if (dilationPx <= 0) return bounds;
  return [
    bounds[0] - dilationPx,
    bounds[1] - dilationPx,
    bounds[2] + dilationPx,
    bounds[3] + dilationPx,
  ];
}

export interface SceneCanvasRenderContext {
  layoutContext: SceneLayoutContext & { resolveRuntime: GraphRuntimeResolver };
  renderSlot: 'geometry' | 'material';
  colormap: ColormapName;
  aaMode: 'none' | 'fwidth' | 'msaa2x';
  aaWidth: number;
  arcCoverageMode: ArcCoverageMode;
  sdfTheme: SdfTheme;
}

/** Collect all visible LayerNodes in draw order (back to front). Recurses into InstanceNode component scenes. */
function collectDrawItems(
  scene: Scene,
  layoutResult: LayoutResult,
  renderContext: SceneCanvasRenderContext,
  ids: SceneNodeId[],
  out: DrawItem[],
  ancestors: Set<string>,
  clip: SceneClip = NO_CLIP,
  pathPrefix = '',
  inheritedSerialized: Record<string, unknown> = {},
  inheritedRuntime: Record<string, unknown> = {},
): void {
  for (const id of ids) {
    const node = scene.nodes[id];
    if (!node) continue;
    const path = nodePath(pathPrefix, id);
    if (!shownInSession(renderContext.layoutContext.session, path, node.visible)) continue;
    const ownerRuntime = renderContext.layoutContext.behaviorParams?.[path] ?? {};

    if (node.kind === 'layer') {
      const layout = layoutResult.layoutMap[id];
      const runtime = renderContext.layoutContext.resolveRuntime(node.layerId);
      if (layout && runtime) {
        out.push({
          itemKey: path,
          layerNode: node,
          layout,
          runtime,
          clip,
          paramOverrides: mergeRuntimeParamOverrides(
            inheritedSerialized,
            node.paramOverrides,
            inheritedRuntime,
            ownerRuntime,
          ),
        });
      }
    } else if (node.kind === 'frame') {
      const frameLayout = node.clip ? layoutResult.layoutMap[id] : undefined;
      const childClip = frameLayout ? clipWithin(clip, frameLayout) : clip;
      collectDrawItems(scene, layoutResult, renderContext, node.childIds, out, ancestors, childClip, pathPrefix, inheritedSerialized, { ...inheritedRuntime, ...ownerRuntime });
    } else if (node.kind === 'instance') {
      if (ancestors.has(node.componentId)) continue; // cycle guard
      const componentDef = renderContext.layoutContext.componentLibrary[node.componentId];
      if (!componentDef) continue;
      const outerLayout = layoutResult.layoutMap[id];
      if (!outerLayout) continue;

      const innerW = outerLayout.localBounds.w;
      const innerH = outerLayout.localBounds.h;
      if (innerW <= 0 || innerH <= 0) continue;

      const innerSpace: Space = { min: { w: 0, h: 0 }, max: { w: innerW, h: innerH } };
      const innerLayout = computeLayout(componentDef.scene, innerSpace, {
        ...renderContext.layoutContext,
        parentTransform: outerLayout.fromLocal,
        behaviorPathPrefix: placementPrefix(path),
      });

      const newAncestors = new Set([...ancestors, node.componentId]);
      collectDrawItems(
        componentDef.scene,
        innerLayout,
        renderContext,
        componentDef.scene.rootIds,
        out,
        newAncestors,
        clip,
        placementPrefix(path),
        { ...inheritedSerialized, ...node.paramOverrides },
        { ...inheritedRuntime, ...ownerRuntime },
      );
    }
  }
}

/**
 * Render the scene into a 2D canvas context.
 *
 * Traverses the scene tree, rendering each LayerNode within its computed
 * aabb using the graph runtime. FrameNodes are structural only (no visual).
 * InstanceNodes recurse into the component's internal scene.
 */

export function renderSceneCanvas(
  ctx2d: CanvasRenderingContext2D,
  width: number,
  height: number,
  scene: Scene,
  layoutResult: LayoutResult,
  renderContext: SceneCanvasRenderContext,
): void {
  const perf = isPerfLogEnabled();
  const tStart = perf ? performance.now() : 0;
  const { renderSlot, colormap, aaMode, aaWidth, arcCoverageMode, sdfTheme } = renderContext;
  // Everything that changes painted output globally (and is not captured in
  // per-item snapshots) must be part of this key — a mismatch recreates the
  // painter and forces a full repaint.
  const settingsKey = JSON.stringify({
    gen: cacheGeneration, width, height, renderSlot, colormap, aaMode, aaWidth, arcCoverageMode, sdfTheme,
  });

  let cache = painterCaches.get(ctx2d.canvas);
  let fullDirtyFromRecreate = false;
  if (!cache || cache.settingsKey !== settingsKey) {
    if (cache) {
      painterFinalizer?.unregister(cache);
      cache.painter.free();
    }
    cache = {
      painter: new ScenePainter({
        width,
        height,
        renderSlot,
        colormap,
        aaMode,
        clearColor: sdfTheme.clearColor,
        fillOutside: sdfTheme.fillOutside,
        fillInside: sdfTheme.fillInside,
        strokeColor: sdfTheme.strokeColor,
        isoColor: sdfTheme.isoColor,
        isoFrequency: ISO_FREQUENCY,
        isoHalfWidth: ISO_HALF_WIDTH,
        isoIntensity: ISO_INTENSITY,
        strokeHalfWidth: STROKE_HALF_WIDTH,
      }),
      settingsKey,
      snapshots: new Map(),
      order: [],
    };
    painterCaches.set(ctx2d.canvas, cache);
    painterFinalizer?.register(cache, cache.painter, cache);
    fullDirtyFromRecreate = true;
  }
  const painter = cache.painter;

  // Collect visible layer items in draw order
  const items: DrawItem[] = [];
  collectDrawItems(scene, layoutResult, renderContext, scene.rootIds, items, new Set());

  // Pass 1: prepare per-item paint args + dirty snapshots (same math as the
  // old single-pass renderer; items that would be skipped get no snapshot, so
  // appearing/disappearing is handled by the add/remove rect rules).
  interface PreparedItem {
    item: DrawItem;
    snapshot: ItemSnapshot;
    mergedParams: Record<string, unknown>;
    allocatedSize: Size;
    args: LayerPaintArgs;
  }
  const prepared: PreparedItem[] = [];
  for (const item of items) {
    const { layout, runtime, layerNode, clip, paramOverrides } = item;
    const clipRect = clip.scissor;
    const allocatedSize: Size = { w: layout.localBounds.w, h: layout.localBounds.h };
    if (allocatedSize.w <= 0 || allocatedSize.h <= 0) continue;

    const mergedParams = { ...renderContext.layoutContext.params, ...paramOverrides };

    // drawBounds = [minX, minY, maxX, maxY] offsets; minX/minY are negative
    // for expansion. The runtime-dependent piece stays here; the layout math
    // is shared with native renderers in the engine (scene/layer_paint.rs).
    const dilationPx = runtime.dynamicDrawBoundsDilationPx?.(renderSlot) ?? 0;
    const drawBounds = dilateDrawBounds(
      runtime.materialDrawBounds(mergedParams, allocatedSize, worldScaleOf(layout.fromLocal)),
      dilationPx,
    );

    const plan = planLayerPaint({
      layout: {
        fromLocal: Array.from(layout.fromLocal),
        toLocal: Array.from(layout.toLocal),
        localBounds: layout.localBounds,
        quad: layout.quad,
        aabb: layout.aabb,
      },
      fit: layerNode.fit,
      aaWidth,
      drawBounds,
      clipRect: clipRect ? [clipRect.x0, clipRect.y0, clipRect.x1, clipRect.y1] : undefined,
      canvas: [width, height],
    }) as ({ skip: boolean } & Omit<LayerPaintArgs, 'clipLocal'>);
    if (plan.skip) continue;
    const { skip: _skip, ...planned } = plan;
    // The plan cuts at the scissor; a turned clipping frame's box is the
    // painter's to cut at, per pixel, in the layer's own units.
    const args: LayerPaintArgs = { ...planned, clipLocal: clipInLayer(clip, layout.toLocal) };

    prepared.push({
      item,
      mergedParams,
      allocatedSize,
      snapshot: {
        paintedRect: { x0: args.x0, y0: args.y0, x1: args.x1, y1: args.y1 },
        paramValues: pickParamValues(runtime.paramKeys, mergedParams),
        paramsRef: renderContext.layoutContext.params,
        overridesRef: paramOverrides,
        contentKey: runtime.contentKey,
        lfw: args.toLocal,
        allocatedW: args.allocatedW,
        allocatedH: args.allocatedH,
        offsetX: args.offsetX,
        offsetY: args.offsetY,
        wsX: args.wsX,
        wsY: args.wsY,
        density: args.density,
        layerFw: args.layerFw,
        fit: layerNode.fit,
        clipRect,
        clipLocal: args.clipLocal,
      },
      args,
    });
  }

  // Pass 2: diff against the previous frame.
  const dirtyRegion = fullDirtyFromRecreate
    ? 'full'
    : computeDirtyRegion(
        cache.snapshots,
        cache.order,
        prepared.map(entry => ({ itemKey: entry.item.itemKey, snapshot: entry.snapshot })),
      );
  cache.snapshots = new Map(prepared.map(entry => [entry.item.itemKey, entry.snapshot]));
  cache.order = prepared.map(entry => entry.item.itemKey);

  if (dirtyRegion === null) {
    if (perf) console.log(`[perf] dirty: skip; renderSceneCanvas total: ${(performance.now() - tStart).toFixed(1)}ms`);
    return;
  }
  const dirty: PaintedRect = dirtyRegion === 'full'
    ? { x0: 0, y0: 0, x1: width, y1: height }
    : {
        x0: Math.max(0, Math.floor(dirtyRegion.x0)),
        y0: Math.max(0, Math.floor(dirtyRegion.y0)),
        x1: Math.min(width, Math.ceil(dirtyRegion.x1)),
        y1: Math.min(height, Math.ceil(dirtyRegion.y1)),
      };
  if (dirty.x1 <= dirty.x0 || dirty.y1 <= dirty.y0) {
    if (perf) console.log('[perf] dirty: clipped to empty; skip');
    return;
  }

  // Pass 3: clear the dirty rect and repaint every item intersecting it,
  // back-to-front, each clipped to the dirty rect.
  if (!fullDirtyFromRecreate) {
    painter.clearRect(dirty.x0, dirty.y0, dirty.x1, dirty.y1);
  }
  let paintedCount = 0;
  for (const { item, snapshot, mergedParams, allocatedSize, args } of prepared) {
    const r = snapshot.paintedRect;
    const x0 = Math.max(r.x0, dirty.x0);
    const y0 = Math.max(r.y0, dirty.y0);
    const x1 = Math.min(r.x1, dirty.x1);
    const y1 = Math.min(r.y1, dirty.y1);
    if (x1 <= x0 || y1 <= y0) continue;

    const tLayer = perf ? performance.now() : 0;
    item.runtime.paintTile?.(painter, mergedParams, allocatedSize, { ...args, x0, y0, x1, y1 });
    paintedCount += 1;
    if (perf) {
      console.log(`[perf] paintTile ${item.layerNode.layerId} rect=${x1 - x0}x${y1 - y0}: ${(performance.now() - tLayer).toFixed(1)}ms`);
    }
  }

  const tFinish = perf ? performance.now() : 0;
  const dw = dirty.x1 - dirty.x0;
  const dh = dirty.y1 - dirty.y0;
  const bytes = painter.readRect(dirty.x0, dirty.y0, dirty.x1, dirty.y1);
  const img = new ImageData(new Uint8ClampedArray(bytes), dw, dh);
  if (ctx2d.canvas.width !== width) ctx2d.canvas.width = width;
  if (ctx2d.canvas.height !== height) ctx2d.canvas.height = height;
  ctx2d.putImageData(img, dirty.x0, dirty.y0);
  if (perf) {
    const areaPct = ((dw * dh) / (width * height)) * 100;
    console.log(
      `[perf] dirty rect ${dw}x${dh} (${areaPct.toFixed(1)}%), items ${paintedCount}/${prepared.length}; `
      + `readRect+blit: ${(performance.now() - tFinish).toFixed(1)}ms; renderSceneCanvas total: ${(performance.now() - tStart).toFixed(1)}ms (${width}x${height})`,
    );
  }
}
